"use client";

// Свод инвентаризации — вариант Б со сводкой из А (выбор Марата 23.09).
//
// Было: серый прямоугольник, пять одинаковых столбцов, триста ровных строк.
// Стало:
//   · сверху четыре числа, ради которых инвентаризацию и проводят —
//     замерено из каталога, стоимость остатка, недостача к учёту, кто мерил;
//   · ниже — позиции по категориям, как их и считают: бар, кухня,
//     хозтовары. У группы свой итог и своя недостача; группа, где всё
//     сошлось, свёрнута — её не надо листать;
//   · в строке — факт, учёт кассы, разница и её цена;
//   · «не замерено» — отдельной группой, не нулями.
//
// Цвет по правилу четырёх смыслов: красным — только расхождение ЗА порогом
// заведения (норма inventory_dev_pct, S2-А), мелкое — серым; «не замерено» —
// фиолетовым (внимание, а не беда).

import { useEffect, useMemo, useState } from "react";
import { api, type ApiResult } from "./api";
import type { InventorySheet } from "@/lib/inventory/sheet-db";
import { fmtSummaryTotal } from "@/lib/inventory/format";
import { Chevron, ErrorNote, Spinner, bQuietS } from "./ui";

const rub = (v: number) => `${Math.round(v).toLocaleString("ru-RU")} ₽`;
const signedRub = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.round(Math.abs(v)).toLocaleString("ru-RU")} ₽`;
const dateRu = (iso: string) => iso.split("-").reverse().join(".");

function qty(v: number, unit: string): string {
  const abs = Math.abs(v);
  const s = abs.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${s} ${unit}`;
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

type Row = InventorySheet["groups"][number]["rows"][number];

/**
 * `fetcher` — откуда брать свод. По умолчанию кабинет (getInventorySheet под
 * учёткой сотрудника); консоль оператора передаёт свой запрос с ключом
 * администратора — вид один и тот же, чтобы оператор видел ровно то, что
 * видит владелец.
 */
export default function InventorySheetView({
  sessionId,
  fetcher,
}: {
  sessionId: string;
  fetcher?: (sessionId: string) => Promise<ApiResult<InventorySheet>>;
}) {
  const [data, setData] = useState<InventorySheet | null>(null);
  const [err, setErr] = useState("");
  // Раскрытые группы. По умолчанию открыты те, где есть расхождения; без
  // сверки с кассой расхождений не знаем — тогда открыта первая группа.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [openMissing, setOpenMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    (fetcher ? fetcher(sessionId) : api<InventorySheet>("getInventorySheet", { sessionId })).then((r) => {
      if (!alive) return;
      if (!r.ok) return setErr(r.error);
      setData(r.data);
      const withDiff = r.data.groups.filter((g) => g.diffCount > 0).map((g) => g.category);
      setOpen(new Set(withDiff.length > 0 ? withDiff : r.data.groups.slice(0, 1).map((g) => g.category)));
    });
    return () => {
      alive = false;
    };
    // fetcher — стрелка из родителя; перечитываем только при смене инвентаризации.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const threshold = data?.book.available ? data.book.thresholdPct : null;
  const hasBook = !!data?.book.available;
  const money = !!data?.moneyVisible;

  const allOpen = useMemo(() => !!data && data.groups.every((g) => open.has(g.category)), [data, open]);

  if (err) return <ErrorNote text={err} />;
  if (!data) return <Spinner text="Собираю свод…" />;
  if (data.groups.length === 0 && data.notMeasured.length === 0) {
    return <div className="py-6 text-center text-[13px] text-[var(--op-faint)]">В этой инвентаризации нет замеров</div>;
  }

  const t = data.totals;

  function toggle(cat: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  /** Тон разницы: красный — только за порогом заведения, иначе серый. */
  function diffTone(r: Row): string {
    if (!r.book || r.book.diff === 0) return "text-[var(--op-muted)]";
    const over = threshold !== null && r.book.diffPct !== null && Math.abs(r.book.diffPct) > threshold;
    if (!over) return "text-[var(--op-muted)]";
    return r.book.diff < 0 ? "text-[var(--op-bad)]" : "text-[var(--op-attn)]";
  }

  return (
    <div className="space-y-3">
      {/* ── Четыре числа (из варианта А) ── */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Tile k="Замерено" v={`${t.measured} из ${t.catalog}`} note={t.notMeasured > 0 ? `не замерено ${t.notMeasured}` : "весь каталог"} noteTone={t.notMeasured > 0 ? "attn" : "muted"} />
        <Tile
          k="Стоимость остатка"
          v={t.value !== null ? rub(t.value) : "—"}
          note={!money ? "видно с правом на себестоимость" : t.value === null ? "у части позиций нет цены" : "по закупочным ценам на день замера"}
        />
        <Tile
          k="Недостача к учёту"
          v={t.shortageSum !== null ? (t.shortageSum > 0 ? `−${rub(t.shortageSum)}` : "0 ₽") : "—"}
          vTone={data.book.available && data.book.overThreshold ? "bad" : "fg"}
          note={
            !data.book.available
              ? data.book.reason
              : t.surplusSum !== null && t.surplusSum > 0
                ? `излишек ${rub(t.surplusSum)} · касса на ${dateRu(data.book.bookDay)}`
                : `касса на ${dateRu(data.book.bookDay)}${data.book.stale ? ", снимок старый" : ""}`
          }
        />
        <Tile k="Мерили" v={t.measurers.length > 0 ? t.measurers.join(", ") : "—"} small />
      </div>

      {data.groups.length > 1 && (
        <div className="flex justify-end">
          <button
            className={bQuietS}
            onClick={() => setOpen(allOpen ? new Set() : new Set(data.groups.map((g) => g.category)))}
          >
            {allOpen ? "Свернуть все" : "Развернуть все"}
          </button>
        </div>
      )}

      {/* ── Группы по категориям (вариант Б) ── */}
      <div className="rounded-2xl bg-[var(--op-surface)]">
        {data.groups.map((g, gi) => {
          const isOpen = open.has(g.category);
          const clean = hasBook && g.diffCount === 0;
          return (
            <section key={g.category} className={gi > 0 ? "border-t border-[var(--op-card-line)]" : ""}>
              <button
                className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3 text-left"
                onClick={() => toggle(g.category)}
                aria-expanded={isOpen}
              >
                <Chevron open={isOpen} className="self-center" />
                <span className="text-[15px] font-semibold text-[var(--op-fg)]">{g.category}</span>
                <span className="text-[12px] text-[var(--op-faint)]">
                  {g.rows.length} {plural(g.rows.length, "позиция", "позиции", "позиций")}
                  {g.value !== null && money ? ` · ${rub(g.value)}` : ""}
                  {hasBook && money && g.shortageSum > 0 ? ` · недостача ${rub(g.shortageSum)}` : ""}
                  {clean ? " · расхождений нет ✓" : hasBook && g.diffCount > 0 ? ` · расхождений ${g.diffCount}` : ""}
                </span>
              </button>
              {isOpen && (
                <div className="overflow-x-auto px-4 pb-3">
                  <table className="w-full text-[13px] tabular-nums">
                    <thead>
                      <tr className="text-left text-[11px] text-[var(--op-faint)]">
                        <th className="py-1.5 pr-3 font-medium">Позиция</th>
                        <th className="py-1.5 pr-3 text-right font-medium">Факт</th>
                        {hasBook && <th className="py-1.5 pr-3 text-right font-medium">По учёту</th>}
                        {hasBook && <th className="py-1.5 pr-3 text-right font-medium">Разница</th>}
                        {money && <th className="py-1.5 pr-3 text-right font-medium">{hasBook ? "В деньгах" : "Стоимость"}</th>}
                        <th className="py-1.5 font-medium">Кто мерил</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r) => (
                        <tr key={r.itemId} className="border-t border-[var(--op-card-line)]">
                          <td className="py-2 pr-3">
                            <div className="font-medium text-[var(--op-fg)]">{r.name}</div>
                            {(r.mode === "formula" || r.note) && (
                              <div className="text-[11px] text-[var(--op-faint)]">
                                {r.mode === "formula" ? `${r.fullCount} запеч. + ${fmtSummaryTotal(r.openMl, "мл")} откр.` : ""}
                                {r.mode === "formula" && r.note ? " · " : ""}
                                {r.note}
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right font-medium text-[var(--op-fg)]">{fmtSummaryTotal(r.total, r.unit)}</td>
                          {hasBook && (
                            <td className="py-2 pr-3 text-right text-[var(--op-muted)]">
                              {r.book ? fmtSummaryTotal(r.book.book, r.unit) : <span className="text-[var(--op-faint)]">нет в кассе</span>}
                            </td>
                          )}
                          {hasBook && (
                            <td className={`py-2 pr-3 text-right ${diffTone(r)}`}>{r.book ? (r.book.diff === 0 ? "0" : qty(r.book.diff, r.unit)) : "—"}</td>
                          )}
                          {money && (
                            <td className={`py-2 pr-3 text-right ${hasBook ? diffTone(r) : "text-[var(--op-muted)]"}`}>
                              {hasBook
                                ? r.book?.diffSum != null && r.book.diffSum !== 0
                                  ? signedRub(r.book.diffSum)
                                  : "—"
                                : r.value !== null
                                  ? rub(r.value)
                                  : "—"}
                            </td>
                          )}
                          <td className="py-2 text-[var(--op-muted)]">{r.measuredBy.length > 0 ? r.measuredBy.join(", ") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}

        {/* «Не замерено» — отдельной группой, фиолетовой: это вопрос к смене,
            а не ноль на складе. */}
        {data.notMeasured.length > 0 && (
          <section className="border-t border-[var(--op-card-line)]">
            <button
              className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3 text-left"
              onClick={() => setOpenMissing((v) => !v)}
              aria-expanded={openMissing}
            >
              <Chevron open={openMissing} className="self-center" />
              <span className="text-[15px] font-semibold text-[var(--op-fg)]">Не замерено</span>
              <span className="rounded-full bg-[var(--op-attn-soft)] px-2 py-0.5 text-[11px] font-semibold text-[var(--op-attn)]">
                {t.notMeasured}
              </span>
              <span className="text-[12px] text-[var(--op-faint)]">есть в каталоге, в этой инвентаризации их нет</span>
            </button>
            {openMissing && (
              <ul className="grid gap-x-6 gap-y-1 px-4 pb-3 text-[13px] sm:grid-cols-2 lg:grid-cols-3">
                {data.notMeasured.map((m) => (
                  <li key={m.itemId} className="flex justify-between gap-2 border-t border-[var(--op-card-line)] py-1.5">
                    <span className="text-[var(--op-fg)]">{m.name}</span>
                    <span className="text-[12px] text-[var(--op-faint)]">{m.category ?? ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function Tile({
  k,
  v,
  note,
  vTone = "fg",
  noteTone = "muted",
  small = false,
}: {
  k: string;
  v: string;
  note?: string;
  vTone?: "fg" | "bad";
  noteTone?: "muted" | "attn";
  small?: boolean;
}) {
  return (
    <div className="rounded-2xl bg-[var(--op-surface)] px-4 py-3">
      <div className="text-[12px] text-[var(--op-faint)]">{k}</div>
      <div
        className={[
          small ? "mt-1 text-[15px] font-medium" : "mt-0.5 text-[22px] font-semibold leading-tight",
          "tabular-nums",
          vTone === "bad" ? "text-[var(--op-bad)]" : "text-[var(--op-fg)]",
        ].join(" ")}
      >
        {v}
      </div>
      {note && <div className={`mt-0.5 text-[11px] ${noteTone === "attn" ? "text-[var(--op-attn)]" : "text-[var(--op-faint)]"}`}>{note}</div>}
    </div>
  );
}
